import { useEffect, useRef, useState } from "react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { PenLine, Eraser, Loader2, Trash2 } from "lucide-react";

function Pad({ onReady }) {
  const ref = useRef(null);
  const drawing = useRef(false);
  const dirty = useRef(false);

  useEffect(() => {
    const cv = ref.current;
    const r = cv.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    cv.width = r.width * dpr; cv.height = r.height * dpr;
    const ctx = cv.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.6; ctx.lineCap = "round"; ctx.lineJoin = "round"; ctx.strokeStyle = "#111";
    onReady({
      clear: () => { ctx.clearRect(0, 0, cv.width, cv.height); dirty.current = false; },
      isEmpty: () => !dirty.current,
      toBlob: () => new Promise((res) => cv.toBlob(res, "image/png")),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pos = (e) => { const r = ref.current.getBoundingClientRect(); return [e.clientX - r.left, e.clientY - r.top]; };
  const down = (e) => { ref.current.setPointerCapture(e.pointerId); drawing.current = true; const ctx = ref.current.getContext("2d"); ctx.beginPath(); ctx.moveTo(...pos(e)); };
  const move = (e) => { if (!drawing.current) return; const ctx = ref.current.getContext("2d"); ctx.lineTo(...pos(e)); ctx.stroke(); dirty.current = true; };
  const up = () => { drawing.current = false; };

  return (
    <canvas ref={ref} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up}
      className="w-full h-56 sm:h-64 bg-white rounded-xl touch-none cursor-crosshair border-2 border-dashed border-neutral-300"
      data-testid="signature-canvas" />
  );
}

export const SignatureSlot = ({ bookingId, kind, label, signature, onChange }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const pad = useRef(null);
  const src = signature ? `${API}/admin/bookings/${bookingId}/signature/${kind}?auth=${localStorage.getItem("an_token")}&t=${encodeURIComponent(signature.signed_at)}` : null;

  const save = async () => {
    if (!pad.current || pad.current.isEmpty()) { toast.error("Fai firmare il cliente nel riquadro"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", new File([await pad.current.toBlob()], "firma.png", { type: "image/png" }));
      const { data } = await api.post(`/admin/bookings/${bookingId}/signature/${kind}`, fd);
      onChange({ signed_at: data.signed_at });
      setOpen(false);
      toast.success("Firma salvata nel contratto");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm("Cancellare questa firma?")) return;
    try { await api.delete(`/admin/bookings/${bookingId}/signature/${kind}`); onChange(null); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid={`signature-slot-${kind}`}>
      <div className="h-16 flex items-end justify-center relative">
        {src ? <img src={src} alt="Firma cliente" className="max-h-16 max-w-full object-contain" data-testid={`signature-img-${kind}`} />
          : <Button type="button" size="sm" onClick={() => setOpen(true)} className="no-print rounded-full bg-[#E63946] hover:bg-[#D62828] text-white h-8 text-xs" data-testid={`signature-sign-btn-${kind}`}>
              <PenLine className="w-3.5 h-3.5 mr-1.5" />Firma sul telefono
            </Button>}
        {src && (
          <button type="button" onClick={remove} className="no-print absolute right-0 bottom-1 w-7 h-7 rounded-full bg-neutral-200 hover:bg-[#E63946] hover:text-white flex items-center justify-center transition-colors" data-testid={`signature-delete-${kind}`} aria-label="Cancella firma">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <div className="border-t border-neutral-800 pt-1">{label}</div>
      {signature && <div className="text-[8px] text-neutral-500">Firmato digitalmente il {new Date(signature.signed_at).toLocaleString("it-IT")}</div>}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl bg-[#12141A] border-white/10" data-testid={`signature-dialog-${kind}`}>
          <DialogHeader><DialogTitle className="font-display uppercase">{label}</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground -mt-2">Il cliente firma con il dito nel riquadro bianco. Gira il telefono in orizzontale per più spazio.</p>
          {open && <Pad onReady={(p) => { pad.current = p; }} />}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button type="button" variant="outline" onClick={() => pad.current?.clear()} className="rounded-full border-white/15 bg-transparent" data-testid="signature-clear-btn">
              <Eraser className="w-4 h-4 mr-1.5" />Cancella
            </Button>
            <Button type="button" onClick={save} disabled={busy} className="rounded-full bg-[#E63946] hover:bg-[#D62828] text-white" data-testid="signature-confirm-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Conferma firma"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
